/**
 * NMS Judge.me Widget Re-initialization Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Handles re-initialization of Judge.me widgets after dynamic content updates
 */

class NMSJudgemeReinit extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];

  constructor(element = document) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.componentId = 'judgeme-reinit-global'; // REQUIRED: Unique identifier

    // REQUIRED: Store in global registry for external access
    window.neemans = window.neemans || {};
    window.neemans.judgemeReinit = window.neemans.judgemeReinit || {};
    window.neemans.judgemeReinit[this.componentId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init();
    this.#bindGlobalEvents();

    // Delay to ensure Judge.me has populated widget data
    setTimeout(() => {
      this.#hideEmptyWidgetsOnLoad();
    }, 500);

    this.#isInitialized = true;
  }

  /**
   * Format a single widget's badge text
   * @private
   */
  #formatBadgeWidget(widget) {
    const badge = widget.querySelector('.jdgm-prev-badge');

    if (badge) {
      const rating = badge.getAttribute('data-average-rating');
      const reviews = badge.getAttribute('data-number-of-reviews');
      const reviewCount = parseInt(reviews) || 0;

      // Hide widget if no meaningful reviews
      if (reviewCount === 0 || rating === '0' || rating === '0.0' || rating === '0.00') {
        widget.style.display = 'none';
      } else {
        // Format the text for widgets with reviews
        const textSpan = widget.querySelector('.jdgm-prev-badge__text');
        if (textSpan && rating && reviews) {
          textSpan.textContent = `${rating} (${reviews})`;
        }
        widget.style.visibility = 'visible';
        widget.style.display = '';
      }
    }
  }

  /**
   * Format all Judge.me badges on the page
   * @private
   */
  #formatAllBadges() {
    const widgets = document.querySelectorAll('.jdgm-reviews-badge');

    for (const widget of widgets) {
      this.#formatBadgeWidget(widget);
    }
  }

  /**
   * Hide empty Judge.me widgets on initial page load and format text
   * @private
   */
  #hideEmptyWidgetsOnLoad() {
    this.#formatAllBadges();
  }

  #bindGlobalEvents() {
    // Listen for color content updates
    const colorUpdateHandler = event => this.#handleColorContentUpdate(event);
    document.addEventListener('nms:color-content-updated', colorUpdateHandler);
    this.#eventListeners.push({ element: document, event: 'nms:color-content-updated', handler: colorUpdateHandler });

    // Listen for theme loading end (after facet/filter updates)
    const loadingEndHandler = event => this.#handleThemeLoadingEnd(event);
    document.documentElement.addEventListener('theme:loading:end', loadingEndHandler);
    this.#eventListeners.push({ element: document.documentElement, event: 'theme:loading:end', handler: loadingEndHandler });

    // Handle theme editor reloads
    const sectionLoadHandler = event => this.#handleSectionLoad(event);
    document.addEventListener('shopify:section:load', sectionLoadHandler);
    this.#eventListeners.push({ element: document, event: 'shopify:section:load', handler: sectionLoadHandler });
  }

  #handleColorContentUpdate(event) {
    const { container, source } = event.detail;

    try {
      this.#reinitializeWidgetsInContainer(container);
    } catch (error) {
      this.handleError(error, 'Failed to handle color content update for Judge.me widgets');
    }
  }

  #handleThemeLoadingEnd(event) {
    try {
      // After facet/filter/pagination updates, new product cards are rendered
      // Re-initialize all Judge.me widgets on the page
      setTimeout(() => {
        this.#reinitializeJudgemeWidgets();

        // Format all badges after re-initialization
        setTimeout(() => {
          this.#formatAllBadges();
        }, 500); // Wait for Judge.me to populate widget data
      }, 100); // Small delay to ensure DOM is fully updated
    } catch (error) {
      this.handleError(error, 'Failed to handle theme loading end for Judge.me widgets');
    }
  }

  #handleSectionLoad(event) {
    if (event.target.querySelector('.jdgm-reviews-badge')) {
      try {
        this.#reinitializeJudgemeWidgets();
      } catch (error) {
        this.handleError(error, 'Failed to handle section load for Judge.me widgets');
      }
    }
  }

  #reinitializeWidgetsInContainer(container) {
    // Find Judge.me widgets in the updated container
    const judgemeWidgets = container.querySelectorAll('.jdgm-reviews-badge');

    if (judgemeWidgets.length > 0) {
      // Immediately hide widgets during re-initialization to prevent any flash
      judgemeWidgets.forEach(widget => {
        widget.style.visibility = 'hidden';
        widget.style.display = 'none';
        widget.classList.remove('jdgm--done-setup');
        widget.removeAttribute('data-impressions-tracked');
        widget.removeAttribute('data-views-tracked');
      });

      // Re-initialize Judge.me widgets
      setTimeout(() => {
        this.#reinitializeJudgemeWidgets();

        // Force trigger Judge.me's internal setup completion
        setTimeout(() => {
          judgemeWidgets.forEach(widget => {
            this.#finalizeWidgetSetup(widget);
          });
        }, 300);
      }, 100);
    }
  }

  #reinitializeJudgemeWidgets() {
    if (typeof window.JudgeMe !== 'undefined' && window.JudgeMe.widgets) {
      window.JudgeMe.widgets.init();
    } else if (typeof window.jdgm !== 'undefined') {
      if (typeof window.jdgm.loadAllWidgets === 'function') {
        window.jdgm.loadAllWidgets();
      } else if (typeof window.jdgm.loadWidgets === 'function') {
        window.jdgm.loadWidgets();
      }
    }
  }

  #finalizeWidgetSetup(widget) {
    if (!widget.classList.contains('jdgm--done-setup')) {
      widget.classList.add('jdgm--done-setup');
    }

    // Use shared formatting logic
    this.#formatBadgeWidget(widget);
  }

  // Public API methods
  reinitializeAll() {
    this.#reinitializeJudgemeWidgets();
  }

  reinitializeInContainer(container) {
    this.#reinitializeWidgetsInContainer(container);
  }

  destroy() {
    // Clean up event listeners
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];

    // Clean up global reference
    if (window.neemans.judgemeReinit?.[this.componentId]) {
      delete window.neemans.judgemeReinit[this.componentId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSJudgemeReinit() {
  if (!window.NMSBaseClass) {
    console.error('NMSJudgemeReinit: NMSBaseClass required');
    return;
  }

  // Create global instance
  const instance = new NMSJudgemeReinit();
  instance.init();
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSJudgemeReinit);
} else {
  initNMSJudgemeReinit();
}

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSJudgemeReinit = NMSJudgemeReinit;

// Global initialization function for other components to use
window.initNMSJudgemeReinit = initNMSJudgemeReinit;
